import { useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { cn } from '../../lib/cn'
import { Button } from './button'

/**
 * Modal panel: overlay foreground/45 + blur, content rounded-xl bg-popover
 * ring-1, zoom-in at 100ms. Esc and overlay click close. Dialogs are flat.
 */
export function Dialog({ open, onClose, title, description, children, footer, size = 'sm', className }: {
  open: boolean
  onClose: () => void
  title?: ReactNode
  description?: ReactNode
  children?: ReactNode
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl'
  className?: string
}) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev }
  }, [open, onClose])
  if (!open) return null
  const widths = { sm: 'sm:max-w-sm', md: 'sm:max-w-md', lg: 'sm:max-w-2xl', xl: 'sm:max-w-4xl' }
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center" role="dialog" aria-modal="true">
      <div className="animate-dw-fade-in absolute inset-0 bg-foreground/45 supports-backdrop-filter:backdrop-blur-sm" onClick={onClose} />
      <div className={cn('animate-dw-zoom-in relative flex w-full max-h-[90vh] flex-col gap-4 overflow-hidden rounded-xl bg-popover p-4 text-sm text-popover-foreground ring-1 ring-foreground/10', widths[size], className)}>
        {(title || description) ? (
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              {title ? <h2 className="text-base leading-snug font-semibold">{title}</h2> : null}
              {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
            </div>
            <Button variant="ghost" size="icon-sm" aria-label="Close" onClick={onClose} className="-mr-1 -mt-1"><X /></Button>
          </div>
        ) : null}
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
        {footer ? <div className="flex flex-wrap items-center justify-end gap-2">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  )
}
